import React, { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { DayFrequency, ViewMode } from '../../types';

interface FrequencyChartProps {
  dayData: DayFrequency;
  selectedDay: string;
  viewMode: ViewMode;
}

interface ChartDataPoint {
  hour: string;
  count: number;
  amount: number;
}

const FrequencyChart: React.FC<FrequencyChartProps> = ({ dayData, selectedDay, viewMode }) => {
  const chartData: ChartDataPoint[] = useMemo(() => {
    const data: ChartDataPoint[] = [];
    
    // Create all 24 hour buckets
    for (let i = 0; i < 24; i++) {
      const hourRange = `${i}-${i + 1}`;
      const bucket = dayData.buckets[hourRange];
      
      data.push({
        hour: `${i}:00`,
        count: bucket?.count || 0,
        amount: bucket?.totalAmount || 0
      });
    }
    
    return data;
  }, [dayData]);

  const formatCurrency = (value: number) => {
    return `$${value.toLocaleString('es-CL', { maximumFractionDigits: 0 })}`;
  };

  const formatCount = (value: number) => {
    return value.toString();
  };

  const formatTime = (minutes: number) => {
    const roundedMinutes = Math.round(minutes);
    if (roundedMinutes >= 60) {
      const hours = Math.floor(roundedMinutes / 60);
      const mins = roundedMinutes % 60;
      if (mins === 0) {
        return `${hours} ${hours === 1 ? 'hora' : 'horas'}`;
      }
      return `${hours} ${hours === 1 ? 'hora' : 'horas'} ${mins} min`;
    }
    return `${roundedMinutes} min`;
  };

  return (
    <div className="mp-chart-card">
      <div className="mp-chart-header flex items-center justify-between">
        <span>{selectedDay}</span>
        <span className="text-xs font-medium normal-case tracking-normal" style={{ color: 'var(--text-color)', opacity: 0.5 }}>
          Tiempo promedio de atención: {formatTime(dayData.averageTimeToPayMinutes)}
        </span>
      </div>
      <div className="mp-chart-body" style={{ height: 420 }}>
      
      <ResponsiveContainer width="100%" height={400}>
        <LineChart
          data={chartData}
          margin={{ top: 5, right: 60, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(0, 0, 0, 0.1)" />
          <XAxis
            dataKey="hour"
            stroke="#666"
            style={{ fontSize: '0.75rem' }}
          />
          
          {(viewMode === 'both' || viewMode === 'frequency') && (
            <YAxis
              yAxisId="left"
              stroke="#FC7556"
              label={{ value: 'Cantidad de recibos', angle: -90, position: 'insideLeft', style: { fontSize: '0.75rem' } }}
              tickFormatter={formatCount}
              style={{ fontSize: '0.75rem' }}
            />
          )}
          
          {(viewMode === 'both' || viewMode === 'amount') && (
            <YAxis
              yAxisId={viewMode === 'both' ? 'right' : 'left'}
              orientation={viewMode === 'both' ? 'right' : 'left'}
              stroke="#4A90E2"
              label={{ value: 'Monto Total', angle: viewMode === 'both' ? 90 : -90, position: viewMode === 'both' ? 'insideRight' : 'insideLeft', style: { fontSize: '0.65rem' } }}
              tickFormatter={formatCurrency}
              style={{ fontSize: '0.65rem' }}
            />
          )}
          
          <Tooltip
            contentStyle={{
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              border: '1px solid rgba(0, 0, 0, 0.1)',
              borderRadius: '8px',
              fontSize: '0.85rem'
            }}
            formatter={(value: number, name: string) => {
              if (name === 'count') {
                return [value, 'Recibos'];
              }
              return [formatCurrency(value), 'Monto'];
            }}
          />
          
          <Legend
            wrapperStyle={{ fontSize: '0.85rem' }}
            formatter={(value) => value === 'count' ? 'Cantidad de recibos' : 'Monto Total'}
          />
          
          {(viewMode === 'both' || viewMode === 'frequency') && (
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="count"
              stroke="#FC7556"
              strokeWidth={2}
              dot={{ fill: '#FC7556', r: 4 }}
              activeDot={{ r: 6 }}
              name="count"
            />
          )}
          
          {(viewMode === 'both' || viewMode === 'amount') && (
            <Line
              yAxisId={viewMode === 'both' ? 'right' : 'left'}
              type="monotone"
              dataKey="amount"
              stroke="#4A90E2"
              strokeWidth={2}
              dot={{ fill: '#4A90E2', r: 4 }}
              activeDot={{ r: 6 }}
              name="amount"
            />
          )}
        </LineChart>
      </ResponsiveContainer>
      </div>
    </div>
  );
};

export default FrequencyChart;
